import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getApartments, getApartmentUsers, getContributions, createContribution, deleteContribution, USE_MOCK_DATA } from '../services/api';
import { mockApartments, mockContributions, mockApartmentUsers } from '../services/mockData';

export default function Contributions() {
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [apartmentUsers, setApartmentUsers] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ apartmentUserId: '', flatNumber: '', amount: '', contributionDate: '', notes: '' });
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (USE_MOCK_DATA) {
      const userApartments = [mockApartments[0]];
      setApartments(userApartments);
      setSelectedApartment(userApartments[0].id);
      return;
    }
    getApartments().then(res => {
      setApartments(res.data);
      if (res.data.length > 0) setSelectedApartment(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedApartment) return;
    if (USE_MOCK_DATA) {
      setContributions(mockContributions);
      setApartmentUsers(mockApartmentUsers);
      return;
    }
    getContributions(selectedApartment, month, year).then(res => setContributions(res.data)).catch(() => {});
    getApartmentUsers(selectedApartment).then(res => setApartmentUsers(res.data)).catch(() => {});
  }, [selectedApartment, month, year]);

  const handleSubmit = async () => {
    if (USE_MOCK_DATA) {
      const au = apartmentUsers.find(u => u.id === parseInt(form.apartmentUserId));
      setContributions(prev => [...prev, {
        id: Date.now(), apartmentId: selectedApartment, apartmentName: 'Sunshine Residency',
        apartmentUserId: parseInt(form.apartmentUserId), flatNumber: form.flatNumber,
        userName: au?.userName || '', amount: parseFloat(form.amount),
        contributionDate: form.contributionDate, month, year, notes: form.notes || null,
        createdAt: new Date().toISOString()
      }]);
    } else {
      await createContribution({
        apartmentId: selectedApartment,
        apartmentUserId: parseInt(form.apartmentUserId),
        flatNumber: form.flatNumber,
        amount: parseFloat(form.amount),
        contributionDate: form.contributionDate,
        month,
        year,
        notes: form.notes || null
      });
      getContributions(selectedApartment, month, year).then(res => setContributions(res.data));
    }
    setOpen(false);
    setForm({ apartmentUserId: '', flatNumber: '', amount: '', contributionDate: '', notes: '' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this contribution?')) {
      if (USE_MOCK_DATA) {
        setContributions(prev => prev.filter(c => c.id !== id));
      } else {
        await deleteContribution(id);
        getContributions(selectedApartment, month, year).then(res => setContributions(res.data));
      }
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Contributions by Flat</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Apartment</InputLabel>
            <Select value={selectedApartment} label="Apartment" onChange={(e) => setSelectedApartment(e.target.value)}>
              {apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
              {Array.from({ length: 12 }, (_, i) => <MenuItem key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
              {Array.from({ length: 5 }, (_, i) => <MenuItem key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          {isAdmin() && (
            <Button variant="contained" startIcon={<Add />} fullWidth onClick={() => setOpen(true)}>Add Contribution</Button>
          )}
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Flat</TableCell>
              <TableCell>Resident</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Notes</TableCell>
              {isAdmin() && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {contributions.map(c => (
              <TableRow key={c.id}>
                <TableCell>{new Date(c.contributionDate).toLocaleDateString()}</TableCell>
                <TableCell>{c.flatNumber}</TableCell>
                <TableCell>{c.userName}</TableCell>
                <TableCell align="right">₹{c.amount.toFixed(2)}</TableCell>
                <TableCell>{c.notes}</TableCell>
                {isAdmin() && (
                  <TableCell>
                    <IconButton onClick={() => handleDelete(c.id)} color="error"><Delete /></IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {contributions.length > 0 && (
              <TableRow>
                <TableCell colSpan={3}><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>₹{contributions.reduce((sum, c) => sum + c.amount, 0).toFixed(2)}</strong></TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Contribution</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Flat / Resident</InputLabel>
            <Select value={form.apartmentUserId} label="Flat / Resident" onChange={(e) => {
              const selected = apartmentUsers.find(au => au.id === e.target.value);
              setForm({ ...form, apartmentUserId: e.target.value, flatNumber: selected?.flatNumber || '' });
            }}>
              {apartmentUsers.map(au => <MenuItem key={au.id} value={au.id}>{au.flatNumber} - {au.userName}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Flat Number" margin="normal" value={form.flatNumber} onChange={(e) => setForm({ ...form, flatNumber: e.target.value })} />
          <TextField fullWidth label="Amount" type="number" margin="normal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <TextField fullWidth label="Date" type="date" margin="normal" InputLabelProps={{ shrink: true }} value={form.contributionDate} onChange={(e) => setForm({ ...form, contributionDate: e.target.value })} />
          <TextField fullWidth label="Notes" margin="normal" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
